import {wait} from "../lib/utils";
import { decode as msgpackDecode } from "@msgpack/msgpack";
import {random} from "lodash";
import {NotAuthorized, SearchTimeoutError, ServerError, ServerOfflineError, ServerStartingException, CORSError} from "./errors";
import {BETA, DEV, getConfig} from "../lib/configMgr";
import Analytics from "../lib/Analytics";
import * as Sentry from "@sentry/react";

// Connect/response timeout for non-streaming LLM calls (chat POST before stream_id is
// returned, oneshot bedrock calls). Shared so every LLM entrypoint agrees on how long to
// wait before giving up (PLEX-6160/6161). Avoid inadvertent timeouts while debugging.
// Must exceed RequestBuilder.fetch()'s own 429/503 backoff budget (up to ~245s across
// MAX_BACKOFF_RETRIES) — otherwise this outer race fires and surfaces a false "timed out"
// error while the inner retry is still legitimately recovering from a transient gateway
// blip (WEBAPP-10 / PLEX-5837).
export const LLM_REQUEST_TIMEOUT = DEV ? 6000000 : 300000;

const GATEWAY_STATUSES = [502, 503, 504];
// Header used to detect whether a response actually reached Plex infrastructure (see
// networkBlocked below). CloudFront stamps this on every response it serves — cache hit,
// S3 origin, nginx origin, or a WAF block — independent of nginx location config, so it's
// a more reliable signal than any header nginx itself adds.
const INFRA_REQUEST_ID_HEADER = 'x-amz-cf-id';

const {env} = getConfig();

let requestID = 0;

// ServerOfflineError is caught-and-swallowed at nearly every call site (retry logic,
// AppStatusStore), so it rarely reaches GlobalErrorBoundary/unhandledRejection and
// almost never becomes a PostHog $exception. Report it here, at the point it's thrown,
// so it's traced regardless of how (or whether) a caller handles it. dev/staging hit
// this constantly when backends are down for maintenance, so only report from prod
// (mirrors the non-prod suppression intent in src/sentry.js).
const reportServerOffline = error => {
  if (env === 'prod') {
    Analytics.exception(error);
  }
};

export const toJSON = response => {
  // Make sure that if the response is not JSON we still deliver some useful information about the error
  if (!response) {
    throw Error("Attempted to convert empty response to JSON");
  }
  // 204 No Content / 205 Reset Content — no body, nothing to parse. Returning
  // an empty object lets DELETE/PUT callers chain `.then(...)` without
  // pretending the empty body was malformed JSON.
  if (response.status === 204 || response.status === 205) {
    return Promise.resolve({});
  }
  try {
    const contentType = response?.headers.get('content-type') || '';
    const isMsgPack = contentType.includes("application/x-msgpack");
    // Also match structured-syntax suffixes (application/problem+json from
    // Connexion validation errors); otherwise those bodies were treated as
    // "non-JSON" and surfaced as a misleading "Expected JSON response" error
    // plus Sentry noise (related to WEBAPP-M).
    const isJSON = contentType.includes("application/json") || contentType.includes("+json");
    return (isMsgPack
            ? response.arrayBuffer().then(buffer => msgpackDecode(new Uint8Array(buffer)))
            : isJSON ? response.json() : response?.text().then(text => {
              // If we got HTML or non-JSON text back, this is an error condition
              // Don't silently return the text - throw a proper error
              const status = response.status;
              const url = response.url;
              const textPreview = text.substring(0, 200);
              const contentType = response?.headers.get('content-type') || 'unknown content-type';

              const responseHeaders = {
                server: response?.headers.get('server'),
                xCache: response?.headers.get('x-cache'),
                xRequestId: response?.headers.get('x-request-id'),
                contentLength: response?.headers.get('content-length'),
                via: response?.headers.get('via'),
              };

              if (GATEWAY_STATUSES.includes(status)) {
                console.warn(`Gateway error [${status}] from ${url}`, responseHeaders);
                const offlineError = new ServerOfflineError(`Server is temporarily unavailable [${status}]`);
                reportServerOffline(offlineError);
                throw offlineError;
              }

              const errorMsg = `Expected JSON response but received ${contentType}`;
              const context = {url, status, contentType, preview: textPreview, ...responseHeaders};
              console.warn(`${errorMsg} from ${url}:`, textPreview);
              Sentry.captureMessage(`Non-JSON response: ${contentType} [${status}] from ${url}`, {
                level: 'warning',
                tags: {response_status: status, response_server: responseHeaders.server || 'unknown', response_x_cache: responseHeaders.xCache || 'none'},
                contexts: {response: context},
              });
              throw new ServerError(
                `${errorMsg}: ${textPreview}`,
                status >= 400 ? status : 502,
                context
              );
            }))
      .catch(error => {
        if (error instanceof ServerError || error instanceof ServerOfflineError) {
          throw error;
        }
        const status = response.status;
        const url = response.url;
        const details = response.statusText ? `: ${response.statusText}` : ""
        const content = `Error parsing server response from ${url}${details}: (${error})`;
        if (error instanceof TypeError) {
          // Body-read failure (e.g. network drop mid-fetch, "Failed to fetch") — same
          // transient-network condition the fetch()-level catch classifies as offline.
          console.warn("toJSON body read failed", error, content);
          const offlineError = new ServerOfflineError("Server temporarily unavailable, try again later", { cause: error });
          reportServerOffline(offlineError);
          throw offlineError;
        }
        console.error("toJSON error", error, content, response);
        throw new ServerError(content, status >= 400 ? status : 400);
      });
  }
  catch(error) {
    console.error("toJSON call failed", error);
    throw error;
  }
};


export class RequestBuilder {
  _method = "GET";
  _url = null;
  _headers = new Headers(DEV || BETA ? {} : {"Accept": "application/x-msgpack; */*"});
  //_headers = new Headers();
  _body = null;
  _failed_auth_tokens = new Set();
  _fetchParams = {};

  constructor(url, fetchParams = {}) {
    this._url = url;
    this._id = ++requestID;
    this._fetchParams = fetchParams;
  }

  get id() {
    return this._id;
  }

  get url() {
    return this._url;
  }

  get auth() {
    return this._authData;
  }

  withAccept = (type) => {
    this._headers.set("Accept",  + `${type};${this._headers.get("Accept")}`);
  }

  withHeader = (name, value) => {
    this._headers.set(name, value);
    return this;
  };

  withContentType = (contentType) => {
    return this.withHeader("Content-Type", contentType);
  }

  withJSONContentType = () => {
    return this.withContentType("application/json");
  };

  withAuthorization = (authData) => {
    this._authData = authData;
    return this;
  };

  asGET = () => {
    this._method = "GET";
    return this;
  };

  asPOST = body => {
    this._method = "POST";
    this._body = body;
    return this;
  };

  asPATCH = body => {
    this._method = "PATCH";
    this._body = body;
    return this;
  };

  asPUT = body => {
    this._method = "PUT";
    this._body = body;
    return this;
  };

  asDELETE = () => {
    this._method = "DELETE";
    return this;
  };

  fetch = (retries = 0, backoff = 5000, cannedResponse = null) => {
    const MAX_BACKOFF_RETRIES = 10;
    const MAX_AUTH_RETRIES = 3;
    const MAX_GATEWAY_RETRIES = 2; // Retry 502/504 errors
    const authData = this._authData;
    if (retries === 0 && authData) {
      //this._authData.jwtToken = 'meant-to-fail';
    }
    if (authData) {
      // Add auth header just before fetch so that we get the most up-to-date value
      const {jwtToken = null} = authData;
      if (!jwtToken) {
        return wait(2000).then(() => this.fetch(retries, backoff));
      }
      this.withHeader("authorization", `Bearer ${jwtToken}`);
    }
    const request = this;
    return fetch(this.url.toString(), {
      method: this._method,
      headers: this._headers,
      body: this._body,
      ...this._fetchParams,
    })
      .then(response => {
        if (!response) {
          throw Error("fetch returned empty response");
        }
        request.response = response;
        if (response.ok) {
          return response;
        }
        // Match application/json AND structured-syntax suffixes like
        // application/problem+json (what Connexion returns for request-validation
        // 400s). Missing the latter meant validation errors fell through to a
        // generic "invalid or unsupported parameters" message that hid Connexion's
        // `detail` (which parameter was rejected and why) — WEBAPP-M.
        const _ctype = response?.headers?.get('content-type') || '';
        const isJSON = _ctype.includes('application/json') || _ctype.includes('+json');
        if ([502, 504].includes(response.status)) {
          // nginx can't connect to server (overloaded or offline)
          if (retries < MAX_GATEWAY_RETRIES) {
            const retryBackoff = 30000; // 30 seconds
            console.debug(`Gateway error [${response.status}], retry ${retries + 1}/${MAX_GATEWAY_RETRIES} after ${retryBackoff / 1000}s (${this.url})`);
            return wait(retryBackoff).then(() => request.fetch(retries + 1, backoff));
          }
          const offlineError = new ServerOfflineError(`Server is temporarily unavailable [${response.status}]`);
          reportServerOffline(offlineError);
          throw offlineError;
        }
        if ([408, 413].includes(response.status)) {
          if (isJSON) {
            return response.json().then(body => {
              throw new SearchTimeoutError(body?.error || undefined);
            });
          }
          throw new SearchTimeoutError();
        }
        if ([429, 503].includes(response.status) && retries < MAX_BACKOFF_RETRIES) {
          console.debug(`Server busy, retry after ${backoff / 1000}s (${this.url})`, response);
          const nextBackoff = Math.min(backoff * 2, 30000) + random(0, 1000, false);
          return wait(backoff).then(() => request.fetch(retries + 1, nextBackoff));
        }
        if (response.status === 503) {
          const offlineError = new ServerOfflineError(`[503] Resource connection failure`);
          reportServerOffline(offlineError);
          throw offlineError;
        }
        if (response.status === 403) {
          const forbiddenUrl = this.url?.toString();
          // Every response that actually reaches our CloudFront distribution carries this
          // header. Its absence means this 403 was injected by something in the client's
          // network path (corporate proxy/firewall) before the request ever reached AWS.
          const networkBlocked = !response.headers?.get(INFRA_REQUEST_ID_HEADER);
          Sentry.captureMessage(`API 403 Forbidden: ${forbiddenUrl}`, {
            level: 'warning',
            tags: {error_type: 'api_forbidden', api_url: forbiddenUrl, network_blocked: networkBlocked},
            contexts: {api_request: {url: forbiddenUrl, method: this._method}},
          });
          // Message is decided here, once, so every call site can just display error.message
          // instead of re-deriving networkBlocked-specific wording themselves.
          const message = networkBlocked
            ? "Your network appears to be blocking access to Plex Search\nCheck with your network administrator or try a different network"
            : "You are not authorized to use this application";
          throw new NotAuthorized(message, {url: forbiddenUrl, method: this._method, networkBlocked});
        }
        if (response.status === 401) {
          const authData = request._authData;
          const failedToken = authData?.jwtToken;
          const abbr = (token) => `${token.slice(0, 6)}...${token.slice(-6)}`;
          if (!authData) {
            throw new NotAuthorized("No auth data provided");
          }
          if (retries >= MAX_AUTH_RETRIES) {
            throw new NotAuthorized(`Max auth retries exceeded (${abbr(failedToken)})`);
          }
          if (request._failed_auth_tokens.has(failedToken)) {
            console.error(`Already failed once with this token (${abbr(failedToken)})`, request, authData)
            throw new NotAuthorized(`Already failed once with this token (${abbr(failedToken)})`);
          }
          request._failed_auth_tokens.add(failedToken);
          console.debug(
            `Auth failure with [rid=${request._id} url=${request.url}] ${abbr(failedToken)}, refreshing token`,
            this._authData, response);
          return authData.refresh(response)
            .then((updatedAuthData) => {
              if (request._failed_auth_tokens.has(updatedAuthData.jwtToken)) {
                console.error(`No change to JWT token after refresh ${abbr(failedToken)} => ${abbr(
                  updatedAuthData.jwtToken)}: ${request.url}`);
                throw new NotAuthorized("No valid auth tokens available");
              }
              console.info(
                `Repeat request [rid=${this.id} ${request.url}] with new auth ${abbr(updatedAuthData.jwtToken)}`);
              return request.withAuthorization(updatedAuthData).fetch(retries + 1);
            });
        }
        if (response?.headers.get('content-type').includes('application/x-msgpack')) {
          return response.arrayBuffer().then((buffer) => {
            const json = msgpackDecode(new Uint8Array(buffer));
            throw new ServerError(`[${response.status}] ${json.error || JSON.stringify(json)}`, response.status);
          });
        }
        if (response.status === 400) {
          const errString = `Search request failed: invalid or unsupported parameters`;
          if (isJSON) {
            return response.json().then((json) => {
              // Connexion problem+json puts the specific validation failure in
              // `detail` (e.g. "'messages.43.content' is not valid..."); our own
              // handlers use `error`. Surface whichever is present so the user sees
              // WHICH parameter is invalid instead of the generic message (WEBAPP-M).
              const detail = json.detail || json.error || json.title || JSON.stringify(json);
              throw new ServerError(`Invalid request: ${detail}`, response.status, json.data);
            });
          }
          throw new ServerError(errString, response.status, {url: this.url.toString(), body: this._body ? JSON.parse(this._body) : null});
        }
        if (isJSON) {
          return response.json().then((json) => {
            // The body rides along as data: a refused resume's 409 carries the `reason`
            // the client acts on (PLEX-6187).
            throw new ServerError(`[${response.status}] ${json.error || JSON.stringify(json)}`, response.status, json);
          });
        }
        return response?.text().then((text) => {
          throw new ServerError(`[${response.status}] ${text}`, response.status);
        });
      })
      .catch(error => {
        // Fetch errors show up as a TypeError
        if (error instanceof TypeError) {
          // Check if this is a CORS error by examining the error message
          const errorMsg = error.message.toLowerCase();
          const isCORSError = errorMsg.includes('cors') ||
                            errorMsg.includes('blocked by') ||
                            errorMsg.includes('preflight') ||
                            errorMsg.includes('access-control');

          if (isCORSError) {
            const details = {
              url: this.url.toString(),
              method: this._method,
              headers: Array.from(this._headers.entries()),
              originalError: error.message
            };
            console.error("CORS error detected:", details);
            throw new CORSError(
              `CORS policy blocked request to ${this.url.toString()}. ` +
              `Check that the server allows the request method and headers.`,
              details
            );
          }

          const offlineError = new ServerOfflineError("Server temporarily unavailable, try again later", { cause: error });
          reportServerOffline(offlineError);
          throw offlineError;
        }
        if (!(error instanceof NotAuthorized) && error?.status !== 404 && error?.status !== 400 && error?.status !== 409) {
          console.error("Request error", error);
        }
        // NotAuthorized is handled by the auth layer (token refresh / redirect to login);
        // emitting api-error for it produces misleading noise (e.g. cancel-storm on session expiry).
        if (!(error instanceof NotAuthorized)) {
          Analytics.event(Analytics.EVENTS.API_ERROR, {
            error_type: error?.name || error?.constructor?.name || "Unknown",
            error_message: error?.message,
            status: error?.status,
            url: this.url?.toString(),
            method: this._method,
          });
        }
        throw error;
      });
  };
}
